import { BLOCKS, Document } from '@contentful/rich-text-types'
import { ComponentCtaButtonColor, ComponentCtaButtonType, TypeComponentCtaButton } from '../../types'
import { ComponentContentTypes } from '../../types/content-types'
import { createEntry } from '../../utils/contentful-entry'
import type { PatchPageStructure } from '../types'
import { TypeSectionHero, TypeSectionGroup } from '../../types'
import { getConfig } from '../../../config'
import { BackgroundColor } from '../../types/background'

const config = getConfig()

const editorUrl = config.urls.aiEditorNewDoc

// Editor CTA for test_editor_cta variant
const editorCtaLinkEntry = createEntry(ComponentContentTypes.ComponentLink, 'SignedInHeroEditorCtaLink', {
  targetUrl: editorUrl,
  targetTab: 'New ( _blank )',
  internalName: editorUrl,
  appendCurrentQueryParams: true,
})

const editorCtaButtonEntry = createEntry(ComponentContentTypes.ComponentCtaButton, 'SignedInHeroEditorCta', {
  ctaText: {
    data: {},
    content: [
      {
        data: {},
        content: [
          {
            data: {},
            marks: [
              {
                type: 'bold',
              },
            ],
            value: 'Start Writing',
            nodeType: 'text',
          },
        ],
        nodeType: BLOCKS.PARAGRAPH,
      },
    ],
    nodeType: BLOCKS.DOCUMENT,
  },
  internalName: 'SignedInHeroEditorCta',
  color: ComponentCtaButtonColor.WhiteTextGreenButton,
  type: ComponentCtaButtonType.Link,
  targetLink: editorCtaLinkEntry,
})

// Desktop app CTA for test_persistent_client_cta variant (green button)
const desktopAppCtaButtonEntry = createEntry(ComponentContentTypes.ComponentCtaButton, 'SignedInHeroDesktopAppCta', {
  ctaText: {
    data: {},
    content: [
      {
        data: {},
        content: [
          {
            data: {},
            marks: [
              {
                type: 'bold',
              },
            ],
            value: 'Download desktop app',
            nodeType: 'text',
          },
        ],
        nodeType: BLOCKS.PARAGRAPH,
      },
    ],
    nodeType: BLOCKS.DOCUMENT,
  },
  internalName: 'DownloadDesktopAppCta',
  color: ComponentCtaButtonColor.WhiteTextGreenButton,
  type: ComponentCtaButtonType.LlamaInstall,
})

// Browser extension CTA for test_persistent_client_cta variant
// Creates button with appropriate color based on background
const createBrowserExtensionCtaButton = (backgroundColor?: BackgroundColor): TypeComponentCtaButton => {
  const buttonColor =
    backgroundColor === BackgroundColor.P100_24
      ? ComponentCtaButtonColor.SecondaryDarkBg
      : ComponentCtaButtonColor.Secondary

  return createEntry(ComponentContentTypes.ComponentCtaButton, 'SignedInHeroBrowserExtensionCta', {
    ctaText: {
      data: {},
      content: [
        {
          data: {},
          content: [
            {
              data: {},
              marks: [
                {
                  type: 'bold',
                },
              ],
              value: 'Install browser extension',
              nodeType: 'text',
            },
          ],
          nodeType: BLOCKS.PARAGRAPH,
        },
      ],
      nodeType: BLOCKS.DOCUMENT,
    },
    internalName: 'InstallBrowserExtensionCta',
    color: buttonColor,
    type: ComponentCtaButtonType.ExtensionInstall,
  })
}

// Description for test_editor_cta variant
const editorDescription: Document = {
  data: {},
  content: [
    {
      data: {},
      content: [
        {
          data: {},
          marks: [],
          value:
            "You've signed up for Grammarly. Check grammar, rewrite sentences, and more—all for free—when you write documents in Grammarly Editor.",
          nodeType: 'text',
        },
      ],
      nodeType: BLOCKS.PARAGRAPH,
    },
  ],
  nodeType: BLOCKS.DOCUMENT,
}

// Description for test_persistent_client_cta variant
const persistentClientDescription: Document = {
  data: {},
  content: [
    {
      data: {},
      content: [
        {
          data: {},
          marks: [],
          value:
            "You've signed up for Grammarly. The next step is to get Grammarly up and running on your desktop or web browser so you can get real-time feedback as you write.",
          nodeType: 'text',
        },
      ],
      nodeType: BLOCKS.PARAGRAPH,
    },
  ],
  nodeType: BLOCKS.DOCUMENT,
}

const patchHeroCtAs = (
  heroSection: TypeSectionHero,
  ctaButtons: TypeComponentCtaButton[],
  description: Document
): TypeSectionHero => {
  return {
    ...heroSection,
    fields: {
      ...heroSection.fields,
      description,
      ctAs: ctaButtons,
    },
  }
}

const patchPageStructure: PatchPageStructure = (
  pageStructure,
  experimentService,
  _userService,
  _browserService,
  _facadeService,
  _pageSlug
) => {
  const variant = experimentService.getSignedInPaidLPDesktopEditorPersistentClientCtaAssignment()

  if (variant === 'test_editor_cta' || variant === 'test_persistent_client_cta') {
    const heroSection = pageStructure.fields?.heroSection

    if (!heroSection) {
      return pageStructure
    }

    // Get background color from the hero section
    let backgroundColor: BackgroundColor | undefined

    // Check if heroSection is a sectionGroup or direct sectionHero
    const heroContentType = heroSection.sys?.contentType?.sys?.id
    if (heroContentType === 'sectionGroup') {
      const heroSectionGroup = heroSection as TypeSectionGroup
      backgroundColor = heroSectionGroup.fields?.background?.color
    } else if (heroContentType === 'sectionHero') {
      backgroundColor = (heroSection as TypeSectionHero).fields?.background?.color
    }

    // Determine which CTAs and description to use based on variant
    const ctaButtons: TypeComponentCtaButton[] =
      variant === 'test_editor_cta'
        ? [editorCtaButtonEntry]
        : [desktopAppCtaButtonEntry, createBrowserExtensionCtaButton(backgroundColor)]

    const description = variant === 'test_editor_cta' ? editorDescription : persistentClientDescription

    // Apply the patches based on hero section structure
    if (heroContentType === 'sectionGroup') {
      // Navigate to the actual hero section in rows[0]
      const heroSectionGroup = heroSection as TypeSectionGroup
      const rows = heroSectionGroup.fields?.rows
      if (!rows || rows.length === 0) {
        return pageStructure
      }

      const actualHero = rows[0]
      const actualHeroContentType = actualHero?.sys?.contentType?.sys?.id

      // Only patch if the first row is a sectionHero
      if (actualHeroContentType === 'sectionHero') {
        const patchedActualHero = patchHeroCtAs(actualHero as TypeSectionHero, ctaButtons, description)
        const patchedRows = [patchedActualHero, ...rows.slice(1)]

        return {
          ...pageStructure,
          fields: {
            ...pageStructure.fields,
            heroSection: {
              ...heroSectionGroup,
              fields: {
                ...heroSectionGroup.fields,
                rows: patchedRows,
              },
            },
          },
        }
      }
    } else if (heroContentType === 'sectionHero') {
      // Direct sectionHero, patch it directly
      const patchedHero = patchHeroCtAs(heroSection as TypeSectionHero, ctaButtons, description)

      return {
        ...pageStructure,
        fields: {
          ...pageStructure.fields,
          heroSection: patchedHero,
        },
      }
    }
  }

  return pageStructure
}

export const SignedInHeroEditorCtaExperiment = {
  patchPageStructure,
}
